import { useEffect, useRef, useCallback } from 'react';

export interface SSEConnectionOptions {
  url: string;
  onMessage: (event: MessageEvent) => void;
  onError?: (error: Event) => void;
  onOpen?: () => void;
  enabled?: boolean;
}

export interface SSEConnectionReturn {
  isConnected: boolean;
  reconnect: () => void;
  disconnect: () => void;
}

/**
 * Generic SSE connection hook
 * Manages EventSource lifecycle with automatic cleanup
 */
export function useSSEConnection({
  url,
  onMessage,
  onError,
  onOpen,
  enabled = true,
}: SSEConnectionOptions): SSEConnectionReturn {
  const eventSourceRef = useRef<EventSource | null>(null);
  const isConnectedRef = useRef(false);
  
  // Use refs for callbacks to avoid reconnections when they change
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);
  const onOpenRef = useRef(onOpen);
  
  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage;
    onErrorRef.current = onError;
    onOpenRef.current = onOpen;
  }, [onMessage, onError, onOpen]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      console.log('🔴 [SSE] Disconnecting from:', url);
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      isConnectedRef.current = false;
    }
  }, [url]);

  const connect = useCallback(() => {
    if (!enabled) {
      console.log('⏸️ [SSE] Connection disabled for:', url);
      return;
    }

    // Don't create multiple connections
    if (eventSourceRef.current) {
      console.log('⚠️ [SSE] Already connected to:', url);
      return;
    }

    console.log('🔵 [SSE] Connecting to:', url);
    const eventSource = new EventSource(url, { withCredentials: true });

    eventSource.onopen = () => {
      console.log('✅ [SSE] Connected to:', url);
      isConnectedRef.current = true;
      onOpenRef.current?.();
    };

    eventSource.onmessage = (event) => {
      console.log('📨 [SSE] Message from:', url, event.data);
      onMessageRef.current(event);
    };

    eventSource.onerror = (error) => {
      console.error('❌ [SSE] Error from:', url, error);
      isConnectedRef.current = false;
      onErrorRef.current?.(error);
      
      // EventSource automatically reconnects, but we log the attempt
      if (eventSource.readyState === EventSource.CONNECTING) {
        console.log('🔄 [SSE] Reconnecting to:', url);
      } else if (eventSource.readyState === EventSource.CLOSED) {
        console.log('🔴 [SSE] Connection closed:', url);
      }
    };

    eventSourceRef.current = eventSource;
  }, [url, enabled]);

  const reconnect = useCallback(() => {
    console.log('🔄 [SSE] Manual reconnect requested for:', url);
    disconnect();
    setTimeout(connect, 100);
  }, [connect, disconnect, url]);

  // Establish connection when enabled or URL changes
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, url]); // Only reconnect when enabled or URL changes

  return {
    isConnected: isConnectedRef.current,
    reconnect,
    disconnect,
  };
}

